import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';
import { operationApi } from '../services/api';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import UnitFilter from '../components/UnitFilter';
import PhaseChip from '../components/PhaseChip';
import AarReport from '../components/aar/AarReport';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import type { Operation, OperationEvent, OperationStatus } from '../types/domain';

const STATUS_STYLE: Record<OperationStatus, string> = {
  PLANNED: 'text-gray-300 bg-gray-500/15 border-gray-500/40',
  ACTIVE: 'text-cyan-300 bg-cyan-500/15 border-cyan-500/40',
  COMPLETED: 'text-green-300 bg-green-500/15 border-green-500/40',
  ABORTED: 'text-red-300 bg-red-500/15 border-red-500/40',
};

export default function OperationsPage() {
  const { user } = useAuthStore();
  const { units, getEffectiveUnitIds, isHQ } = useUnitStore();
  const userIsHQ = isHQ(user?.unitId ?? null);
  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId ?? null);
  const singleUnitId = effectiveUnitIds && effectiveUnitIds.length === 1 ? effectiveUnitIds[0] : null;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [aarOpen, setAarOpen] = useState(false);

  const { data: listData, isLoading } = useQuery({
    queryKey: ['operations', singleUnitId],
    queryFn: () => operationApi.getAll(singleUnitId ? { unitId: singleUnitId } : undefined),
    refetchInterval: 8000,
  });

  const { data: detailData } = useQuery({
    queryKey: ['operation', selectedId],
    queryFn: () => operationApi.getById(selectedId as string),
    enabled: !!selectedId,
    refetchInterval: 8000,
  });

  const operations: Operation[] = useMemo(() => {
    let list: Operation[] = listData?.data?.operations || [];
    if (effectiveUnitIds) list = list.filter((o) => effectiveUnitIds.includes(o.unitId));
    return list;
  }, [listData, effectiveUnitIds]);

  const selected: Operation | null = detailData?.data?.operation || null;
  const unitName = (unitId: string) => units.find((u) => u.id === unitId)?.name || 'Unit';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg font-display font-bold text-gray-100">Operations</h1>
          <p className="text-gray-500 text-[11px]">Nightly minor-ops cycle · after-action review timeline</p>
        </div>
        {userIsHQ && <UnitFilter />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Operation list */}
        <div className="lg:col-span-3 rounded-xl border border-command-border bg-command-panel overflow-hidden">
          <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="border-b border-command-border text-gray-400 text-[11px] uppercase tracking-wide">
                  <th className="text-left py-2 px-3 font-semibold">Type</th>
                  <th className="text-left py-2 px-3 font-semibold">Platoon</th>
                  <th className="text-left py-2 px-3 font-semibold">Unit</th>
                  <th className="text-center py-2 px-3 font-semibold">Night</th>
                  <th className="text-left py-2 px-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {operations.map((op) => (
                  <tr
                    key={op.id}
                    onClick={() => setSelectedId(op.id)}
                    className={clsx(
                      'border-b border-command-border/60 cursor-pointer hover:bg-command-bg/40',
                      selectedId === op.id && 'bg-command-accent/10'
                    )}
                  >
                    <td className="py-2 px-3 font-semibold text-gray-100">{op.type}</td>
                    <td className="py-2 px-3 text-gray-300">{op.platoon?.callsign || '—'}</td>
                    <td className="py-2 px-3 text-gray-400">{unitName(op.unitId)}</td>
                    <td className="py-2 px-3 text-center text-gray-400">{op.night ?? '—'}</td>
                    <td className="py-2 px-3">
                      <span className={clsx('px-2 py-0.5 rounded-full text-[10px] font-semibold border', STATUS_STYLE[op.status])}>
                        {op.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {!isLoading && operations.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500 text-sm">
                      No operations recorded yet.
                    </td>
                  </tr>
                )}
                {isLoading && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500 text-sm">
                      Loading operations…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* AAR timeline */}
        <div className="lg:col-span-2 rounded-xl border border-command-border bg-command-panel p-4">
          {!selected ? (
            <p className="text-gray-500 text-sm">Select an operation to view its phase timeline.</p>
          ) : (
            <div>
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-gray-100">
                    {selected.type} · {selected.platoon?.callsign || 'Platoon'}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {unitName(selected.unitId)}
                    {selected.night != null && ` · Night ${selected.night}`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAarOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-command-accent/20 text-command-accent border border-command-accent/40 hover:bg-command-accent/30 flex-shrink-0"
                >
                  <DocumentTextIcon className="w-4 h-4" /> AAR
                </button>
              </div>
              <Timeline events={selected.events || []} />
            </div>
          )}
        </div>
      </div>

      {aarOpen && selected && (
        <AarReport operation={selected} unitLabel={unitName(selected.unitId)} onClose={() => setAarOpen(false)} />
      )}
    </div>
  );
}

function Timeline({ events }: { events: OperationEvent[] }) {
  if (events.length === 0) {
    return <p className="text-gray-500 text-sm">No phase events logged for this operation.</p>;
  }
  const ordered = [...events].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
  return (
    <ol className="relative border-l border-command-border ml-2 space-y-4">
      {ordered.map((ev) => (
        <li key={ev.id} className="ml-4">
          <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-command-accent border border-command-bg" />
          <div className="flex items-center gap-2">
            <PhaseChip phase={ev.phase} size="xs" />
            <span className="text-[11px] text-gray-500">
              {format(new Date(ev.timestamp), 'dd MMM HH:mm')}
            </span>
          </div>
          {ev.note && <p className="text-xs text-gray-300 mt-1">{ev.note}</p>}
          {ev.lat != null && ev.lng != null && (
            <p className="text-[10px] text-gray-600 mt-0.5">
              {ev.lat.toFixed(4)}, {ev.lng.toFixed(4)}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}
